import type DocumentDetailView from '@UI/Store/Documents/Views/DocumentDetailView';
import type { DocumentEntryView } from '@UI/Store/Documents/Views/buildDocumentEntryView';

/** One list element on the way from the document root down to an object. */
export type ContainingItem = {
	readonly listKey: string;
	readonly listTitle: string;
	/** 0-based. */
	readonly index: number;
};

export type ContainedObject = { readonly fieldKey: string } | { readonly listKey: string };

const walk = (
	entries: readonly DocumentEntryView[],
	target: ContainedObject,
	chain: readonly ContainingItem[],
): readonly ContainingItem[] | null => {
	for (const entry of entries) {
		if (entry.type === 'field') {
			if ('fieldKey' in target && entry.key === target.fieldKey) {
				return chain;
			}

			continue;
		}

		if (entry.type === 'group') {
			const found = walk(entry.entries, target, chain);

			if (found !== null) {
				return found;
			}

			continue;
		}

		if ('listKey' in target && entry.key === target.listKey) {
			return chain;
		}

		for (const item of entry.items) {
			const found = walk(item.entries, target, [
				...chain,
				{ listKey: entry.key, listTitle: entry.title, index: item.index },
			]);

			if (found !== null) {
				return found;
			}
		}
	}

	return null;
};

/**
 * The list elements that contain a field or a list, outermost first; `[]` outside any list and `null`
 * when the object is not in the document.
 */
const findContainingItems = (
	document: DocumentDetailView,
	target: ContainedObject,
): readonly ContainingItem[] | null => {
	for (const section of document.sections) {
		const found = walk(section.entries, target, []);

		if (found !== null) {
			return found;
		}
	}

	return null;
};

export default findContainingItems;
